// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// sim.ts — the whole simulation. Pure, integer-only, deterministic from (mode, seed, actionLog).
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// THE ONE MECHANIC, AND THE ARITHMETIC THAT MAKES IT WORK
// ─────────────────────────────────────────────────────────────────────────────────────────────
//
// Every ground cell carries 1.25 items per second for free. Above that the dirt RUTS, rutted dirt
// moves items up to 7.5x slower, and dirt held in the red long enough is permanently SCOURED so it
// ruts even more easily. Rut lives on the CELL, not the belt, so rebuilding a lane inherits its
// history: you do not repair a ruined lane, you route around it.
//
//   rut[c] += (an item entered this tick ? RUT_PER_ENTRY + floor(scour[c]/75) : 0) - 1
//
// The decay is UNCONDITIONAL — it applies on entry ticks too — and that detail is the whole design.
//
// THE FIRST VERSION OF THIS LAW WAS ARITHMETICALLY DEAD, AND IT WAS DEAD IN A WAY NO PLAYTEST WOULD
// HAVE FOUND. It charged +9 on entry and healed -1 only on ticks with no entry, with
// slotTicks = 2 + 2*floor(rut/100). Two independent reviewers derived the same failure and a
// numeric probe confirmed it before a line of this file existed:
//
//   drift/s = 9r - (20 - r) = 10r - 20   =>   a fixed point at exactly r = 2.0 items/s
//
// The throttle was driven by the very quantity it limited, so rut ASYMPTOTED into [400, 500) and
// the drift was strictly negative above it. The top half of the ramp was unreachable on every seed,
// in every mode, under every possible player action: tiers T3 and T4 were dead code, scour could
// never accumulate, and the mode built on inheriting somebody else's scoured ground had no premise.
// Worse, because entry was the only charge, a JAMMED cell — slot 0 permanently occupied, so nothing
// can enter — decayed to zero. Measured: a free-flowing lane read 401 and a jammed one read 9. The
// most congested lane on the board was rendered as the cleanest ground in the factory.
//
// THE FIX IS THE INEQUALITY BELOW, AND IT IS ASSERTED IN tests/mechanism.test.ts (invariant G):
//
//   min capacity (20/15 = 1.333 items/s)  >  worst break-even (20/20 = 1.00 items/s at scour 300)
//
// Because the slowest cell the game can produce still passes MORE than its own break-even rate, an
// oversubscribed cell heats at every rut level. There is no intermediate fixed point, so the ramp
// is monotone all the way to 1000 and the red end is reachable. Verified numerically against a real
// belt chain before this file was written: fed 1.0 items/s a lane settles at rut 0-13, and fed
// 1.8 items/s or more it climbs to 745-937 and throttles to ~1.64 items/s.
//
// WHAT RUT COLOUR MEANS, STATED ONCE SO THE RENDERER CANNOT DRIFT FROM IT: rut is HOW MUCH TRAFFIC
// THIS GROUND HAS CARRIED. It is deliberately NOT a congestion indicator — a jammed lane carries
// nothing, so it stays cool, and that is correct rather than a bug. Congestion has its own channel
// (the fill bar in render.ts and the machine marks), because conflating "this ground is being
// destroyed" with "this lane is backed up" makes both unreadable.

import { ITEM_COUNT, Item, Kind, KindT, MODES, Mode, Quirk, RECIPES, modeOf, rutDeltaFor } from './modes';

export const W = 12;
export const H = 12;
export const N = W * H;
export const HZ = 20;

/** Rut law. Every one of these is pinned by a test; changing one means re-running the sim. */
export const RUT_MAX = 1000;
export const RUT_PER_ENTRY = 16;
export const RUT_DECAY = 1;
export const SCOUR_MAX = 300;
export const SCOUR_THRESHOLD = 800;
export const SCOUR_TICKS_PER_PT = 8;
/** Ticks between slot advances, indexed by tier. The FLOOR (15) is load-bearing — see the header. */
export const SLOT_TICKS = [2, 3, 5, 8, 15] as const;
export const TIER_EDGES = [200, 400, 600, 800] as const;

/** Machines that hold a recipe. Digger is a source and Wharf is a sink, so neither is here. */
export const CRAFTERS = [Kind.Smelter, Kind.Mill, Kind.Jig] as const;

export const COST: Record<number, number> = {
  [Kind.Belt]: 1,
  [Kind.Wye]: 4,
  [Kind.Digger]: 8,
  [Kind.Smelter]: 16,
  [Kind.Mill]: 16,
  [Kind.Jig]: 18,
};

/** Scrip paid by the wharf, per item type. Raw material is worth nothing: shipping it is a mistake. */
export const SCRIP_VALUE = [0, 0, 1, 1, 6];
export const DIG_TICKS = 20;
export const CRAFT_TICKS = 50;
export const OUT_CAP = 4;
export const IN_CAP = 8;
export const SLOTS = 3;

/** N, E, S, W. Index IS the direction. */
export const DX = [0, 1, 0, -1];
export const DY = [-1, 0, 1, 0];

export const cx = (c: number): number => c % W;
export const cy = (c: number): number => (c / W) | 0;
export const idx = (x: number, y: number): number => y * W + x;
export function step(c: number, d: number): number {
  const x = cx(c) + DX[d];
  const y = cy(c) + DY[d];
  return x < 0 || y < 0 || x >= W || y >= H ? -1 : idx(x, y);
}

export function tierOf(rut: number): number {
  let t = 0;
  while (t < 4 && rut >= TIER_EDGES[t]) t++;
  return t;
}
export const slotTicksOf = (rut: number): number => SLOT_TICKS[tierOf(rut)];
/** The rate at which this cell breaks even, in items/s, given its permanent scour. */
export const breakEven = (scour: number): number => (HZ / (RUT_PER_ENTRY + Math.floor(scour / 75))) * 1;

// ── events ───────────────────────────────────────────────────────────────────────────────────
// The audit reads this stream from OUTSIDE and re-derives the rules from it. Emission is off by
// default because 200 balance runs allocating per event turns a 90-second suite into a 40-minute
// one, and that cannot be retrofitted once the sim is written.

export type Ev =
  | { k: 'spawn'; t: number; id: number; item: Item; cell: number }
  | { k: 'move'; t: number; id: number; from: number; fromSlot: number; to: number; toSlot: number }
  | { k: 'consume'; t: number; id: number; item: Item; machine: number }
  | { k: 'produce'; t: number; id: number; item: Item; machine: number }
  | { k: 'sink'; t: number; id: number; item: Item; cell: number }
  | { k: 'destroy'; t: number; ids: number[]; cell: number; reason: 'erase' | 'dry' }
  | { k: 'rutsnap'; t: number; rut: Int16Array; scour: Int16Array }
  | { k: 'place'; t: number; kind: KindT; cell: number; dir: number }
  | { k: 'remove'; t: number; kind: KindT; cell: number };

export interface Layout {
  kind: Uint8Array;
  dir: Uint8Array;
  patch: Uint8Array;
}

export interface State {
  mode: string;
  quirks: Quirk[];
  seed: number;
  tick: number;
  over: boolean;
  scrip: number;
  shipped: number;

  kind: Uint8Array;
  dir: Uint8Array;
  patch: Uint8Array;
  patchLeft: Int32Array;

  rut: Int16Array;
  scour: Int16Array;
  scourAcc: Int32Array;

  /** slot[c*3+s] is 0 for empty, else item+1. */
  slot: Uint8Array;
  slotId: Int32Array;
  lastAdv: Int32Array;

  /** Per-cell machine buffers, ITEM_COUNT wide. */
  inBuf: Int16Array;
  outBuf: Int16Array;
  prog: Int16Array;
  /** Round-robin cursor for a machine's or wye's outputs. */
  rr: Uint8Array;

  /** Teardown returns pieces to stock instead of paying scrip. */
  owned: Int16Array;
  beltBudget: number;

  nextId: number;
  events: Ev[] | null;
  /** Rolling per-cell entry counts for the last 200 ticks, for the strain overlay and diagnosis. */
  flow: Int32Array;
  flowWin: Int32Array;
  crossings: number;
  crossingsRutted: number;
  /** Per-machine-tick state census, for the results screen. */
  censusRun: Int32Array;
  censusStarved: Int32Array;
  censusBlocked: Int32Array;
}

const FLOW_WIN = 200;

export function emptyLayout(): Layout {
  return { kind: new Uint8Array(N), dir: new Uint8Array(N), patch: new Uint8Array(N) };
}

export function createState(mode: string, seed: number, layout: Layout, quirks: Quirk[]): State {
  const m = modeOf(mode);
  const s: State = {
    mode: m.id,
    quirks,
    seed,
    tick: 0,
    over: false,
    scrip: m.startScrip,
    shipped: 0,
    kind: Uint8Array.from(layout.kind),
    dir: Uint8Array.from(layout.dir),
    patch: Uint8Array.from(layout.patch),
    patchLeft: new Int32Array(N),
    rut: new Int16Array(N),
    scour: new Int16Array(N),
    scourAcc: new Int32Array(N),
    slot: new Uint8Array(N * SLOTS),
    slotId: new Int32Array(N * SLOTS),
    lastAdv: new Int32Array(N),
    inBuf: new Int16Array(N * ITEM_COUNT),
    outBuf: new Int16Array(N * ITEM_COUNT),
    prog: new Int16Array(N),
    rr: new Uint8Array(N),
    owned: new Int16Array(16),
    beltBudget: m.beltBudget,
    nextId: 1,
    events: null,
    flow: new Int32Array(N),
    flowWin: new Int32Array(N * 0),
    crossings: 0,
    crossingsRutted: 0,
    censusRun: new Int32Array(N),
    censusStarved: new Int32Array(N),
    censusBlocked: new Int32Array(N),
  };
  s.flowWin = new Int32Array(N);
  if (m.patchReserve > 0) {
    for (let c = 0; c < N; c++) if (s.patch[c]) s.patchLeft[c] = m.patchReserve;
  }
  return s;
}

const emit = (s: State, e: Ev): void => {
  if (s.events) s.events.push(e);
};

// ── queries ──────────────────────────────────────────────────────────────────────────────────

export const isCrafter = (k: number): boolean => k === Kind.Smelter || k === Kind.Mill || k === Kind.Jig;
/** Anything an item can sit on or in. */
export const isCarrier = (k: number): boolean => k === Kind.Belt || k === Kind.Wye;

/** A belt/wye accepts from any neighbour pointing at it; a machine likewise. */
function acceptsAt(s: State, c: number): number {
  const k = s.kind[c];
  if (k === Kind.Belt || k === Kind.Wye) return s.slot[c * SLOTS] === 0 ? 0 : -1;
  return -1;
}

/** Can this machine take one more of `item`? */
function machineAccepts(s: State, c: number, item: Item): boolean {
  const k = s.kind[c];
  if (k === Kind.Wharf) return true;
  if (!isCrafter(k)) return false;
  const r = RECIPES[k];
  if (!r || !r.inputs[item]) return false;
  return s.inBuf[c * ITEM_COUNT + item] < IN_CAP;
}

/**
 * Outputs of a machine or wye: adjacent carriers pointing AWAY from it, plus the wharf.
 *
 * THE WHARF HAS TO BE IN HERE. Without it a machine standing directly against the wharf could never
 * ship: its output buffer filled, it went BACKED-UP, and the lane behind it jammed — on a board
 * where the generator quite reasonably puts the starter smelter next to the wharf. Every seed then
 * opened with a factory that looked correct, moved items, and earned nothing. Machines still do NOT
 * feed each other directly; belts remain the only way between two crafters, which is what keeps
 * routing the game.
 */
function outputsOf(s: State, c: number, out: number[]): number {
  let n = 0;
  for (let d = 0; d < 4; d++) {
    const t = step(c, d);
    if (t < 0) continue;
    if (s.kind[t] === Kind.Wharf) {
      out[n++] = t;
      continue;
    }
    if (!isCarrier(s.kind[t])) continue;
    // The neighbour must point away from us, i.e. not back into this cell.
    if (step(t, s.dir[t]) === c) continue;
    out[n++] = t;
  }
  return n;
}

/**
 * Hand one item from `c` to `target`, which outputsOf() has already vouched for. Returns false if
 * the target is a carrier whose entry slot is full.
 */
function handTo(s: State, target: number, item: Item, id: number): boolean {
  if (s.kind[target] === Kind.Wharf) {
    s.scrip += SCRIP_VALUE[item];
    if (item === 4) s.shipped++;
    emit(s, { k: 'sink', t: s.tick, id, item, cell: target });
    return true;
  }
  if (s.slot[target * SLOTS] !== 0) return false;
  s.slot[target * SLOTS] = item + 1;
  s.slotId[target * SLOTS] = id;
  return true;
}

// ── the tick ─────────────────────────────────────────────────────────────────────────────────

const scratch: number[] = [0, 0, 0, 0];

/**
 * ONE TICK. The order below IS the determinism contract: diggers, then machines, then carriers
 * walked from the wharf backwards, then rut, then the wharf. Every loop is over ascending cell
 * index and nothing iterates a Set or a Map, because insertion order is observable and two runs do
 * not insert in the same order.
 */
export function tick(s: State): void {
  if (s.over) return;
  const t = s.tick;
  const m = modeOf(s.mode);
  const entered = new Uint8Array(N);

  // 1 ── diggers extract onto an adjacent away-pointing belt.
  for (let c = 0; c < N; c++) {
    if (s.kind[c] !== Kind.Digger) continue;
    if (s.patch[c] === 0) continue;
    if (m.patchReserve > 0 && s.patchLeft[c] <= 0) continue;
    if ((t - s.lastAdv[c]) < DIG_TICKS) continue;
    const item = (s.patch[c] - 1) as Item;
    const n = outputsOf(s, c, scratch);
    if (n === 0) continue;
    let placed = false;
    for (let i = 0; i < n && !placed; i++) {
      const target = scratch[(s.rr[c] + i) % n];
      const id = s.nextId;
      if (!handTo(s, target, item, id)) continue;
      s.nextId++;
      if (isCarrier(s.kind[target])) entered[target] = 1;
      s.rr[c] = (s.rr[c] + i + 1) % n;
      s.lastAdv[c] = t;
      if (m.patchReserve > 0) s.patchLeft[c]--;
      emit(s, { k: 'spawn', t, id, item, cell: target });
      placed = true;
    }
  }

  // 2 ── crafters: push finished output out, then consume and advance.
  for (let c = 0; c < N; c++) {
    const k = s.kind[c];
    if (!isCrafter(k)) continue;
    const r = RECIPES[k]!;
    const base = c * ITEM_COUNT;

    // push output
    if (s.outBuf[base + r.output] > 0) {
      const n = outputsOf(s, c, scratch);
      for (let i = 0; i < n && s.outBuf[base + r.output] > 0; i++) {
        const target = scratch[(s.rr[c] + i) % n];
        const id = s.nextId;
        if (!handTo(s, target, r.output, id)) continue;
        s.nextId++;
        if (isCarrier(s.kind[target])) entered[target] = 1;
        s.outBuf[base + r.output]--;
        s.rr[c] = (s.rr[c] + i + 1) % n;
        // A machine pushing its output onto a belt IS a move, and it must be on the wire. Without
        // it the event stream is incomplete: an auditor replaying MOVEs cannot reconstruct which
        // cells were entered, so it cannot re-derive the rut law it is supposed to be checking.
        // The mechanism audit found this as seven unexplained rut mismatches, which is exactly the
        // kind of hole that makes a zero-tolerance invariant quietly meaningless.
        emit(s, { k: 'move', t, id, from: c, fromSlot: 0, to: target, toSlot: 0 });
      }
    }

    if (s.prog[c] > 0) {
      s.prog[c]++;
      if (s.prog[c] > CRAFT_TICKS) {
        if (s.outBuf[base + r.output] < OUT_CAP) {
          s.outBuf[base + r.output]++;
          s.prog[c] = 0;
          emit(s, { k: 'produce', t, id: s.nextId++, item: r.output, machine: c });
          s.censusRun[c]++;
        } else {
          s.prog[c] = CRAFT_TICKS + 1;
          s.censusBlocked[c]++;
        }
      } else s.censusRun[c]++;
      continue;
    }

    // start a craft if the inputs are all present
    let ok = s.outBuf[base + r.output] < OUT_CAP;
    for (let i = 0; ok && i < ITEM_COUNT; i++) if (s.inBuf[base + i] < r.inputs[i]) ok = false;
    if (ok) {
      for (let i = 0; i < ITEM_COUNT; i++) {
        for (let q = 0; q < r.inputs[i]; q++) {
          s.inBuf[base + i]--;
          emit(s, { k: 'consume', t, id: s.nextId++, item: i as Item, machine: c });
        }
      }
      s.prog[c] = 1;
      s.censusRun[c]++;
    } else {
      s.censusStarved[c]++;
    }
  }

  // 3 ── carriers advance. Walk DESCENDING cell index so a freed slot downstream is usable this
  //      tick without an item ever moving more than one slot: each MOVE is still exactly one step.
  for (let c = N - 1; c >= 0; c--) {
    const k = s.kind[c];
    if (!isCarrier(k)) continue;
    if (t - s.lastAdv[c] < slotTicksOf(s.rut[c])) continue;

    const cap = k === Kind.Wye ? 1 : SLOTS;
    const exit = cap - 1;
    const base = c * SLOTS;

    // hand the exit slot downstream
    if (s.slot[base + exit] !== 0) {
      const item = (s.slot[base + exit] - 1) as Item;
      const id = s.slotId[base + exit];
      let moved = false;
      if (k === Kind.Wye) {
        const n = outputsOf(s, c, scratch);
        for (let i = 0; i < n && !moved; i++) {
          const target = scratch[(s.rr[c] + i) % n];
          if (!handTo(s, target, item, id)) continue;
          if (isCarrier(s.kind[target])) entered[target] = 1;
          s.rr[c] = (s.rr[c] + i + 1) % n;
          moved = true;
          emit(s, { k: 'move', t, id, from: c, fromSlot: exit, to: target, toSlot: 0 });
        }
      } else {
        const target = step(c, s.dir[c]);
        if (target >= 0) {
          const tk = s.kind[target];
          if (isCarrier(tk) && acceptsAt(s, target) === 0) {
            s.slot[target * SLOTS] = item + 1;
            s.slotId[target * SLOTS] = id;
            entered[target] = 1;
            moved = true;
            emit(s, { k: 'move', t, id, from: c, fromSlot: exit, to: target, toSlot: 0 });
          } else if (machineAccepts(s, target, item)) {
            if (tk === Kind.Wharf) {
              s.scrip += SCRIP_VALUE[item];
              if (item === 4) s.shipped++;
              emit(s, { k: 'sink', t, id, item, cell: target });
            } else {
              s.inBuf[target * ITEM_COUNT + item]++;
            }
            moved = true;
          }
        }
      }
      if (moved) {
        s.slot[base + exit] = 0;
        s.slotId[base + exit] = 0;
      } else {
        // STUCK. Hold the timer fired so it retries next tick. It charges no rut: a jammed lane
        // carries nothing, so it must not be painted as destroyed ground. Congestion is a separate
        // channel — see the header.
        s.lastAdv[c] = t - slotTicksOf(s.rut[c]);
        continue;
      }
    }
    // shuffle the rest forward
    for (let sl = exit; sl > 0; sl--) {
      if (s.slot[base + sl] === 0 && s.slot[base + sl - 1] !== 0) {
        s.slot[base + sl] = s.slot[base + sl - 1];
        s.slotId[base + sl] = s.slotId[base + sl - 1];
        s.slot[base + sl - 1] = 0;
        s.slotId[base + sl - 1] = 0;
        emit(s, { k: 'move', t, id: s.slotId[base + sl], from: c, fromSlot: sl - 1, to: c, toSlot: sl });
      }
    }
    s.lastAdv[c] = t;
  }

  // 4 ── rut and scour. Only carriers wear; machine and wharf cells are exempt.
  const perEntryBase = RUT_PER_ENTRY + rutDeltaFor(s.quirks);
  for (let c = 0; c < N; c++) {
    if (!isCarrier(s.kind[c])) {
      // Ground under no belt still heals, so a lane you abandon slowly recovers.
      if (s.rut[c] > s.scour[c]) s.rut[c] -= RUT_DECAY;
      continue;
    }
    const eff = perEntryBase + ((s.scour[c] / 75) | 0);
    let r = s.rut[c] + (entered[c] ? eff : 0) - RUT_DECAY;
    if (r < s.scour[c]) r = s.scour[c];
    if (r > RUT_MAX) r = RUT_MAX;
    s.rut[c] = r;
    if (entered[c]) {
      s.flow[c]++;
      s.crossings++;
      if (r >= 600) s.crossingsRutted++;
    }
    if (r >= SCOUR_THRESHOLD) {
      s.scourAcc[c]++;
      const sc = Math.min(SCOUR_MAX, (s.scourAcc[c] / SCOUR_TICKS_PER_PT) | 0);
      s.scour[c] = sc;
    }
  }

  // 5 ── the wharf also takes directly from any adjacent inbound carrier's exit slot handled above.

  s.tick++;
  if (s.tick % FLOW_WIN === 0) {
    s.flowWin.set(s.flow);
    s.flow.fill(0);
  }
  if (s.tick >= m.ticks) s.over = true;
  if (m.patchReserve > 0 && allDry(s)) s.over = true;
}

export function allDry(s: State): boolean {
  for (let c = 0; c < N; c++) if (s.patch[c] && s.patchLeft[c] > 0) return false;
  return true;
}

/** Items/second over the last completed window. */
export function flowRate(s: State, c: number): number {
  return (s.flowWin[c] * HZ) / FLOW_WIN;
}

/** How hard this cell is being pushed relative to what it can carry for free. 1.0 = break-even. */
export function strain(s: State, c: number): number {
  return flowRate(s, c) / breakEven(s.scour[c]);
}

// ── actions ──────────────────────────────────────────────────────────────────────────────────

export type Action =
  | { a: 'place'; kind: KindT; cell: number; dir: number }
  | { a: 'remove'; cell: number };

export function canPlace(s: State, kind: KindT, cell: number): boolean {
  if (cell < 0 || cell >= N) return false;
  const m = modeOf(s.mode);
  const k = s.kind[cell];
  if (k !== Kind.Empty) return false;
  if (s.patch[cell] && kind !== Kind.Digger) return false;
  if (kind === Kind.Digger && !s.patch[cell]) return false;
  if (m.moveOnly) return s.owned[kind] > 0 && (kind !== Kind.Belt || s.beltBudget > 0);
  if (kind === Kind.Belt && s.beltBudget <= 0) return false;
  return s.scrip >= COST[kind];
}

export function place(s: State, kind: KindT, cell: number, dir: number): boolean {
  if (!canPlace(s, kind, cell)) return false;
  const m = modeOf(s.mode);
  if (m.moveOnly) s.owned[kind]--;
  else s.scrip -= COST[kind];
  if (kind === Kind.Belt) s.beltBudget--;
  s.kind[cell] = kind;
  s.dir[cell] = dir;
  s.lastAdv[cell] = s.tick;
  emit(s, { k: 'place', t: s.tick, kind, cell, dir });
  return true;
}

export function remove(s: State, cell: number): boolean {
  const k = s.kind[cell] as KindT;
  if (k === Kind.Empty || k === Kind.Boulder || k === Kind.Wharf) return false;
  const m = modeOf(s.mode);

  // Items standing on a removed carrier are DESTROYED, and that is an event rather than a silent
  // hole. Without it the conservation invariant red-lights on the first delete of every human run,
  // which would make the flagship zero-tolerance check useless.
  const ids: number[] = [];
  for (let sl = 0; sl < SLOTS; sl++) {
    const p = cell * SLOTS + sl;
    if (s.slot[p]) {
      ids.push(s.slotId[p]);
      s.slot[p] = 0;
      s.slotId[p] = 0;
    }
  }
  for (let i = 0; i < ITEM_COUNT; i++) {
    const b = cell * ITEM_COUNT + i;
    for (let q = 0; q < s.inBuf[b] + s.outBuf[b]; q++) ids.push(-1);
    s.inBuf[b] = 0;
    s.outBuf[b] = 0;
  }
  if (ids.length) emit(s, { k: 'destroy', t: s.tick, ids, cell, reason: 'erase' });

  if (m.moveOnly) s.owned[k]++;
  else s.scrip += COST[k] ?? 0;
  if (k === Kind.Belt) s.beltBudget++;
  s.kind[cell] = Kind.Empty;
  s.dir[cell] = 0;
  s.prog[cell] = 0;
  emit(s, { k: 'remove', t: s.tick, kind: k, cell });
  return true;
}

export function apply(s: State, a: Action): boolean {
  return a.a === 'place' ? place(s, a.kind, a.cell, a.dir) : remove(s, a.cell);
}

/** Machine state, exactly as the audit's normative table declares it. */
export type MachineState = 'running' | 'starved' | 'blocked';
export function machineState(s: State, c: number): MachineState {
  const k = s.kind[c];
  const r = RECIPES[k];
  if (!r) return 'running';
  const base = c * ITEM_COUNT;
  if (s.prog[c] > CRAFT_TICKS) return 'blocked';
  if (s.prog[c] > 0) return 'running';
  if (s.outBuf[base + r.output] >= OUT_CAP) return 'blocked';
  for (let i = 0; i < ITEM_COUNT; i++) if (s.inBuf[base + i] < r.inputs[i]) return 'starved';
  return 'running';
}

/** Which input a starved machine is short of — the renderer points the gap at where it should come from. */
export function missingInput(s: State, c: number): Item | -1 {
  const r = RECIPES[s.kind[c]];
  if (!r) return -1;
  const base = c * ITEM_COUNT;
  for (let i = 0; i < ITEM_COUNT; i++) if (s.inBuf[base + i] < r.inputs[i]) return i as Item;
  return -1;
}

export function itemsOnBoard(s: State): number {
  let n = 0;
  for (let p = 0; p < N * SLOTS; p++) if (s.slot[p]) n++;
  for (let p = 0; p < N * ITEM_COUNT; p++) n += s.inBuf[p] + s.outBuf[p];
  return n;
}

/** A cheap, order-independent hash of everything that must replay identically. */
export function hashState(s: State): number {
  let h = 2166136261;
  const mix = (v: number): void => {
    h ^= v & 0xffff;
    h = Math.imul(h, 16777619) >>> 0;
  };
  mix(s.tick);
  mix(s.scrip);
  mix(s.shipped);
  for (let c = 0; c < N; c++) {
    mix(s.kind[c]);
    mix(s.dir[c]);
    mix(s.rut[c]);
    mix(s.scour[c]);
  }
  for (let p = 0; p < N * SLOTS; p++) mix(s.slot[p]);
  for (let p = 0; p < N * ITEM_COUNT; p++) mix(s.inBuf[p] + s.outBuf[p] * 7);
  return h >>> 0;
}

export function run(s: State, ticks: number): void {
  for (let i = 0; i < ticks && !s.over; i++) tick(s);
}

export { Kind, MODES, modeOf };
export type { KindT, Mode, Item };
